import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
const TOKEN =
  "pk.eyJ1IjoidmlnNzcxOWFudG8iLCJhIjoiY21wMTg0a2t2MDJmNTJzb2x6MDFyazEwMSJ9.UkauDvpPrPSqymE795UYkA";
export function driverIsLive(driver) {
  const fix = driver.lastLocation;
  return (
    driver.isActive !== false &&
    fix?.connected === true &&
    Date.now() - fix.ts < 30000
  );
}
export default function DriverMap({ drivers, selectedId, onSelect }) {
  const container = useRef(null);
  const map = useRef(null);
  const markers = useRef(new Map());
  const select = useRef(onSelect);
  select.current = onSelect;
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const currentMarkers = markers.current;
    mapboxgl.accessToken = TOKEN;
    try {
      map.current = new mapboxgl.Map({
        container: container.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [-68.2837, 12.1696],
        zoom: 11,
      });
      map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
      map.current.on("load", () => setReady(true));
      map.current.on("error", () =>
        setError(
          "Map could not load. Driver coordinates remain available in the list.",
        ),
      );
    } catch {
      setError(
        "Map unavailable. Driver coordinates remain available in the list.",
      );
    }
    return () => {
      currentMarkers.forEach((marker) => marker.remove());
      currentMarkers.clear();
      map.current?.remove();
      map.current = null;
    };
  }, []);
  useEffect(() => {
    if (!ready || !map.current) return;
    const visible = new Set();
    for (const driver of drivers) {
      const fix = driver.lastLocation;
      if (!fix || !Number.isFinite(fix.lat) || !Number.isFinite(fix.lng))
        continue;
      visible.add(driver.id);
      let marker = markers.current.get(driver.id);
      if (!marker) {
        const button = document.createElement("button");
        button.type = "button";
        button.style.cssText =
          "width:26px;height:26px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px #0005;cursor:pointer";
        button.addEventListener("click", () => select.current(driver.id));
        marker = new mapboxgl.Marker({ element: button })
          .setLngLat([fix.lng, fix.lat])
          .addTo(map.current);
        markers.current.set(driver.id, marker);
      }
      marker.getElement().style.backgroundColor = driverIsLive(driver)
        ? "#0d5c5c"
        : "#9ca3af";
      marker
        .getElement()
        .setAttribute(
          "aria-label",
          `${driver.name}, ${driver.vehicle || "driver"}, ${driverIsLive(driver) ? "live" : "last known location"}`,
        );
      marker.getElement().title = `${driver.name} · ${driver.vehicle || "No vehicle"}`;
      marker.setLngLat([fix.lng, fix.lat]);
    }
    for (const [id, marker] of markers.current)
      if (!visible.has(id)) {
        marker.remove();
        markers.current.delete(id);
      }
  }, [drivers, ready]);
  useEffect(() => {
    const fix = drivers.find(
      (driver) => driver.id === selectedId,
    )?.lastLocation;
    if (ready && Number.isFinite(fix?.lng) && Number.isFinite(fix?.lat))
      map.current?.flyTo({ center: [fix.lng, fix.lat], zoom: 14 });
    // Follow selection changes, without resetting an admin's camera on every ping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, ready]);
  return (
    <div className="relative">
      <div
        ref={container}
        className="h-[420px] w-full rounded-xl overflow-hidden"
        aria-label="Driver location map"
      />
      {error && (
        <p role="alert" className="p-3 bg-amber-50 text-amber-900">
          {error}
        </p>
      )}
      <p className="text-xs text-gray-500 mt-2">
        Green: live within 30 seconds. Gray: last-known location.
      </p>
    </div>
  );
}
